import React, { useMemo, useState } from 'react';
import { InputGroup, LovableModal, Textarea } from '@nearst/ui';
import * as Sentry from '@sentry/react';
import { alreadyReported } from '@services/lib/mutation-reporter';
import { hideProducts } from '@services/stock';
import { useShop } from '@services/ShopProvider';

import { sendAnalytics } from '../../../../utils/sendAnalytics';

import styles from './BarcodeVisibilityModal.module.scss';
import { ResultsSection } from './ResultsSection';
import { ChannelSelection } from './ChannelSelection';

function buildChannelFlags(availableChannels, mode, selectedChannels, enabled) {
	if (mode === 'all') {
		return Object.fromEntries(availableChannels.map((ch) => [ch.id, enabled]));
	}
	return Object.fromEntries(
		Object.entries(selectedChannels)
			.filter(([, isSelected]) => isSelected)
			.map(([id]) => [id, enabled])
	);
}

const BarcodeVisibilityModal = ({ open, close, shop, mutate, operation, availableChannels = [] }) => {
	const { data: orgShops } = useShop();
	const [errorMsg, setErrorMsg] = useState('');
	const [barcodeInput, setBarcodeInput] = useState();
	const [loading, setLoading] = useState(false);
	const [progress, setProgress] = useState(null);
	const [results, setResults] = useState([]);
	const [channelMode, setChannelMode] = useState('all');
	const [selectedChannels, setSelectedChannels] = useState({});

	// without a shop id the backend fans the update out to every shop in the organisation
	const shopCount = shop?.id ? 1 : orgShops?.length || 1;

	const showChannelSelection = availableChannels.length >= 2;
	const [affectedChannels, setAffectedChannels] = useState([]);

	const handleClose = () => {
		setResults([]);
		setErrorMsg('');
		setBarcodeInput(undefined);
		setChannelMode('all');
		setSelectedChannels({});
		setAffectedChannels([]);
		setProgress(null);
		close();
	};

	const initialSelectedChannels = useMemo(() => Object.fromEntries(availableChannels.map((ch) => [ch.id, false])), [availableChannels]);

	const handleChange = (e) => {
		const barcodes = e.target.value.split('\n').filter((str) => str);
		if (barcodes.length > 10000) {
			setErrorMsg('Please enter a maximum of 10,000 barcodes');
			setBarcodeInput(barcodes.slice(0, 10000).join('\n'));
		} else {
			setErrorMsg('');
			setBarcodeInput(e.target.value);
		}
		setResults([]);
	};

	const handleSubmit = () => {
		if (!barcodeInput) {
			setErrorMsg('Please enter a valid barcode');
			return;
		}
		const barcodes = barcodeInput.split('\n').filter((str) => str);
		if (barcodes.length > 10000) {
			setErrorMsg('Please enter a maximum of 10,000 barcodes');
			return;
		}

		if (showChannelSelection && channelMode === 'selected') {
			const hasSelection = Object.values(selectedChannels).some(Boolean);
			if (!hasSelection) {
				setErrorMsg('Please select at least one channel');
				return;
			}
		}

		setErrorMsg('');
		setLoading(true);

		const channels = showChannelSelection
			? buildChannelFlags(availableChannels, channelMode, selectedChannels, operation.enabled)
			: null;

		if (channels) {
			const targetedIds = Object.keys(channels);
			setAffectedChannels(availableChannels.filter((ch) => targetedIds.includes(ch.id)));
		} else {
			setAffectedChannels([]);
		}

		hideProducts(shop.id, barcodes, operation.enabled, channels, {
			shopCount,
			onProgress: (done, total) => setProgress({ done, total })
		})
			.then((response) => {
				sendAnalytics('Barcode Form', operation.analyticsAction, shop.name);
				setResults(response);
				mutate();
			})
			.catch((e) => {
				console.error(e);
				if (!alreadyReported(e)) Sentry.captureException(e);
				setErrorMsg(
					e.processed
						? `Something went wrong after updating ${e.processed.toLocaleString()} of ${e.total.toLocaleString()} barcodes. Those changes were applied — it's safe to submit the same list again.`
						: 'Something went wrong processing your request'
				);
				setResults([]);
				if (e.processed) mutate();
			})
			.finally(() => {
				setLoading(false);
				setProgress(null);
			});
	};

	const handleModeChange = (mode) => {
		setChannelMode(mode);
		if (mode === 'selected') {
			setSelectedChannels(initialSelectedChannels);
		}
	};

	const hasResults = results.length > 0;

	return (
		<LovableModal isOpen={open} onDismiss={handleClose} title={operation.title} showDismissButton={false}>
			<div className={styles.container}>
				{hasResults ? (
					<>
						<ResultsSection results={results} operation={operation} affectedChannels={affectedChannels} />
						<p className={styles.disclaimer}>It may take up to an hour before these changes appear across your channels.</p>
					</>
				) : (
					<>
						<InputGroup label="Enter one barcode per line" htmlFor="barcodes">
							<Textarea
								disabled={loading}
								value={barcodeInput}
								onChange={handleChange}
								placeholder="barcode001&#10;barcode002&#10;barcode003"
							/>
						</InputGroup>

						{showChannelSelection && (
							<ChannelSelection
								channels={availableChannels}
								selectedChannels={selectedChannels}
								onSelectionChange={setSelectedChannels}
								mode={channelMode}
								onModeChange={handleModeChange}
								operation={operation}
							/>
						)}

						{errorMsg && <p className={styles.error}>{errorMsg}</p>}

						{loading && progress && (
							<p className={styles.progress}>
								Processing {progress.done.toLocaleString()} of {progress.total.toLocaleString()} barcodes…
							</p>
						)}

						<div className={styles.footer}>
							<button disabled={!barcodeInput || loading} onClick={handleSubmit} className={styles.submitButton}>
								{operation.buttonText}
							</button>
						</div>
					</>
				)}
			</div>
		</LovableModal>
	);
};

export { BarcodeVisibilityModal };
